import { PartialRightshipInspectionCustomerFindings } from '@rightship-tech/rs-inspections-ui/views/partials/customer-findings/partial-rightship-inspection-customer-findings';
import { autoinject, transient, BindingEngine, computedFrom } from "aurelia-framework";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import { RightShipInspectionOverview } from "./steps/overview/rightship-inspection-overview";
import { RightshipInspectionDocuments } from "./steps/documents/rightship-inspection-documents";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipInspectionDeficiencies } from "./steps/deficiencies/rightship-inspection-deficiencies";
import { Reflection } from "../../../../utilities/reflection";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { MenuItem } from "../../../components/menu/menu-item";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import * as moment from "moment";
import { Assignment } from "../../../components/assignment/assignment";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { Claims } from "../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { TasksStepsEnum, TasksConstants } from "../../../../models/tasks/task.model";
import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { RightshipInspectionFinalReport } from "./steps/final-report/rightship-inspection-final-report";
import { RightshipInspectionPreliminaryReport } from "./steps/preliminary-report/rightship-inspection-preliminary-report";
import { RightShipInspectionsService } from "../../../../services/rightship-inspections/rightship-inspections.service";
import { RightShipInspectionStatusEnum, type RightShipInspectionStatusClientModel, RightShipInspectionClientModel, RightShipInspectionType, ChangeInspectionWorkflowRequest } from "../../../../models/rightship-inspections/rightship-inspections.models";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RightShipInspectionHistoryTrail } from "./steps/rightship-inspection-history-trail/rightship-inspection-history-trail";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { EntityItem } from "../entity-item";
import * as Enumerable from "linq";
import { EmailHistory } from "../common-steps/email-history/email-history";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { RatingBaseEntity } from "../common-steps/base-rating-entity";
import { RightShipInspectionInspectorAssignment } from './steps/inspector-assignment/rightship-inspection-inspector-assignment';
import { RightshipInspectionMessageCenter } from "./steps/message-center/rightship-inspection-message-center";
import { RightShipInspectionCrewMatrix } from "./steps/crew-matrix/rightship-inspection-crew-matrix";
import { RightShipInspectionInspectorReport } from "./steps/inspector-report/rightship-inspection-inspector-report";
import { RightshipInspectionNonConformity } from "./steps/non-conformity/rightship-inspection-non-conformity";
import { RightshipInspectionSurvey } from "./steps/inspection-survey/rightship-inspection-survey";
import { RightshipInspectionExecutiveSummary } from "./steps/executive-summary/rightship-inspection-executive-summary";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { CompCorrespondenceModel } from "../../../../models/vessels/vessel.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { RightShipInspectionHelper } from "../../../../helpers/rightship-inspection-helper";
import { AttachmentViewerExecutionModel } from "../../../../models/attachment/attachment.models";
import { RightshipInspectionReport } from "./steps/inspection-report/rightship-inspection-report";

type RightShipInspectionSource = 'vessels' | 'activities';

@autoinject
@transient()
export class RightshipInspection extends RatingBaseEntity<RightShipInspectionClientModel> {

    readonly: boolean = true;
    canChangeRightShipInspectionStatus: boolean;
    canChangeCargillInspectionStatus: boolean;
    assignment: Assignment;
    rightShipInspectionStatuses: LookupValue[];
    entityItem: EntityItem;
    canChangeValidityDate: boolean;
    canEditUpdateRightshipInspection: boolean;
    canSendHybridInspectionEmail: boolean;
    canSendCrewMatrixInspectionEmail: boolean;
    canViewRightShipInspectionCorrespondence: boolean;
    canViewRightShipInspectionEmailHistory: boolean;
    canEditUpdateTankerInspection: boolean;
    canViewTankerInspectionCorrespondence: boolean;
    canViewTankerInspectionEmailHistory: boolean;
    canCreateCargillInspection: boolean;
    canAssignCargillInspection: boolean;
    canAddCargillInspectionDocument: boolean;
    canDeleteCargillInspectionDocument: boolean;
    canViewCargillInspectionHistoryTrail: boolean;
    canEditCargillInspection: boolean;
    canCreateCargillInspectionRfi: boolean;
    canReopenCancelledRSInspection: boolean;
    canCreateInternalRfi: boolean;
    canCreateFormTask: boolean;
    canChangeInspectionCompany: boolean;
    canChangeRSInspectionInspector: boolean;
    canAccessRSInspectorReport: boolean;
    correspondenceSubtaskId: number;
    canChangeInspectionWorkflow: boolean;
    canAssignRightShipInspection: boolean;
    canAssignTankerInspection: boolean;
    isPageLoading: boolean = false;
    isRightshipUser: boolean;
    attachmentViewerExecutionModel: AttachmentViewerExecutionModel;
    private _messageCenterOn: boolean;
    public showInspectionDetailsDrawer: boolean;

    constructor(protected router: Router,
        protected dialogService: DialogClientService,
        protected bindingEngine: BindingEngine,
        protected i18N: I18N,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected validationsClientService: ValidationsClientService,
        protected rightshipInspectionOverview: RightShipInspectionOverview,
        protected rightshipInspectionInspectorAssignment: RightShipInspectionInspectorAssignment,
        protected rightshipInspectionMessageCenter: RightshipInspectionMessageCenter,
        protected rightshipInspectionFinalReport: RightshipInspectionFinalReport,
        protected rightshipInspectionPreliminaryReport: RightshipInspectionPreliminaryReport,
        protected rightshipInspectionDeficiencies: RightShipInspectionDeficiencies,
        protected rightshipInspectionDocuments: RightshipInspectionDocuments,
        protected rightShipInspectionHistoryTrail: RightShipInspectionHistoryTrail,
        protected correspondence: Correspondence,
        protected rightshipInspectionCrewMatrix: RightShipInspectionCrewMatrix,
        protected rightshipInspectionReport: RightshipInspectionReport,
        protected rightshipInspectionInspectorReport: RightShipInspectionInspectorReport,
        protected rightshipInspectionNonConformity: RightshipInspectionNonConformity,
        protected partialRightshipInspectionCustomerFindings: PartialRightshipInspectionCustomerFindings,
        protected rightshipInspectionSurvey: RightshipInspectionSurvey,
        protected rightshipInspectionExecutiveSummary: RightshipInspectionExecutiveSummary,
        protected emailHistory: EmailHistory,
        private inspectionService: RightShipInspectionsService,
        private userClientService: UserClientService,
        private userClaimsService: UserClaimsService,
        protected signalClientService: SignalClientService,
        protected manageVesselService: ManageVesselService,
        private cacheClient: CacheClientService,
        private configService: ConfigService,
        private clipboardCopyService: ClipboardCopyService,
        lookupClientService: LookupClientService,
        private activeMenuItemsService: ActiveMenuItemsService,
        private entityComments: EntityComments,
        private inspectionHelper : RightShipInspectionHelper) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService, signalClientService, manageVesselService);
        this.canChangeRightShipInspectionStatus = userClaimsService.hasPermission(Claims.CanChangeRightShipInspectionStatus);
        this.canChangeValidityDate = userClaimsService.hasPermission(Claims.CanChangeValidityDate);
        this.canEditUpdateRightshipInspection = userClaimsService.hasPermission(Claims.CanEditUpdateRightShipInspection);
        this.canSendHybridInspectionEmail = userClaimsService.hasPermission(Claims.CanSendHybridInspectionEmail);
        this.canSendCrewMatrixInspectionEmail = userClaimsService.hasPermission(Claims.CanSendCrewMatrixInspectionEmail);
        this.canViewRightShipInspectionCorrespondence = userClaimsService.hasPermission(Claims.ViewRightShipInspectionCorrespondence);
        this.canViewRightShipInspectionEmailHistory = userClaimsService.hasPermission(Claims.ViewRightShipInspectionEmailHistory);
        this.canEditUpdateTankerInspection  = userClaimsService.hasPermission(Claims.CanEditUpdateTankerInspection);
        this.canViewTankerInspectionCorrespondence = userClaimsService.hasPermission(Claims.CanViewTankerInspectionCorrespondence);    
        this.canViewTankerInspectionEmailHistory = userClaimsService.hasPermission(Claims.CanViewTankerInspectionEmailHistory);    
        this.canCreateCargillInspection = userClaimsService.hasPermission(Claims.CanCreateCargillInspection);    
        this.canAssignCargillInspection = userClaimsService.hasPermission(Claims.CanAssignCargillInspection);    
        this.canAddCargillInspectionDocument = userClaimsService.hasPermission(Claims.CanAddCargillInspectionDocument);    
        this.canDeleteCargillInspectionDocument = userClaimsService.hasPermission(Claims.CanDeleteCargillInspectionDocument);    
        this.canChangeCargillInspectionStatus = userClaimsService.hasPermission(Claims.CanChangeCargillInspectionStatus);
        this.canViewCargillInspectionHistoryTrail = userClaimsService.hasPermission(Claims.CanViewCargillInspectionHistoryTrail);
        this.canEditCargillInspection = userClaimsService.hasPermission(Claims.CanEditCargillInspection);
        this.canCreateCargillInspectionRfi = userClaimsService.hasPermission(Claims.CanCreateCargillInspectionRfi);
        this.canReopenCancelledRSInspection = userClaimsService.hasPermission(Claims.CanReopenCancelledRSInspection);
        this.canCreateInternalRfi = userClaimsService.hasPermission(Claims.CanCreateInternalRfi);
        this.canCreateFormTask = userClaimsService.hasPermission(Claims.CanCreateFormTask);
        this.canChangeInspectionCompany = userClaimsService.hasPermission(Claims.CanChangeInspectionCompany);
        this.canChangeRSInspectionInspector = userClaimsService.hasPermission(Claims.CanChangeRSInspectionInspector);
        this.canAccessRSInspectorReport = userClaimsService.hasPermission(Claims.CanAccessRSInspectorReport);
        this.canChangeInspectionWorkflow = userClaimsService.hasPermission(Claims.CanChangeInspectionWorkflow);
        this.canAssignRightShipInspection = userClaimsService.hasPermission(Claims.CanAssignRightShipInspection);
        this.canAssignTankerInspection = userClaimsService.hasPermission(Claims.CanAssignTankerInspection);
        this.isRightshipUser = userClaimsService.hasPermission(Claims.RightShipUser);            
        this.rightshipInspectionDeficiencies.getChanges = () => this.getChanges();
        this.rightshipInspectionDeficiencies.clearChanges = () => this.clearChanges();
        this.rightshipInspectionDeficiencies.refreshHistoryTrail = () => this.rightShipInspectionHistoryTrail.refreshData();
        this.rightshipInspectionNonConformity.getChanges = () => this.getChanges();
        this.rightshipInspectionNonConformity.clearChanges = () => this.clearChanges();
        this.rightshipInspectionNonConformity.refreshHistoryTrail = () => this.rightShipInspectionHistoryTrail.refreshData();
        this.rightshipInspectionOverview.changeRightShipInspectionCompany = () => this.changeRightShipInspectionCompany();
        this.rightshipInspectionOverview.changeRightShipInspectionInspector = () => this.changeRightShipInspectionInspector();
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RightShipInspectionStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.rightShipInspectionStatuses = x.activeValues;
        });
        this._messageCenterOn = this.configService.config.settings.messageCenterOn;
    }

    get viewRightShipInspectionCorrespondence() {
        if (this.item) {
            if (this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection)
                return this.userClaimsService.hasPermission(Claims.ViewRightShipInspectionCorrespondence);
            if (this.item.inspectionTypeId == RightShipInspectionType.TankerInspection)
                return this.userClaimsService.hasPermission(Claims.CanViewTankerInspectionCorrespondence);
            if (this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey)
                return this.userClaimsService.hasPermission(Claims.CanViewCargillInspectionCorrespondence);
        }

        return false;
    }

    get viewRightShipInspectionEmailHistory() {
        if (this.item) {
            if (this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection)
                return this.userClaimsService.hasPermission(Claims.ViewRightShipInspectionEmailHistory);
            if (this.item.inspectionTypeId == RightShipInspectionType.TankerInspection)
                return this.userClaimsService.hasPermission(Claims.CanViewTankerInspectionEmailHistory);
            if (this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey)
                return this.userClaimsService.hasPermission(Claims.CanViewCargillInspectionEmailHistory);
        }

        return false;
    }

    toggleInspectionDetailsDrawer(model: AttachmentViewerExecutionModel) {
        this.attachmentViewerExecutionModel = model;
        this.showInspectionDetailsDrawer = !this.showInspectionDetailsDrawer;
    }

    @computedFrom(Reflection.getPropertyPath<RightshipInspection>(z => z.item),
    Reflection.getPropertyPath<RightshipInspection>(z => z.item.inspectionTypeId))
    public get inspectionType() {
        if (this.item) {
            switch (this.item.inspectionTypeId) {
                case 1: return "RightShip";
                case 2: return "Cargill";
                case 3: return "Tanker";
            }
        }

        return "";
    }

    @computedFrom("isCargill", "canAssignCargillInspection")
    public get ifCargillCheckCanAssignInspection() : boolean {
        return this.isCargill ? this.canAssignCargillInspection : false; 
    }

    @computedFrom("isTankerInspection", "canAssignTankerInspection")
    public get ifTankerCheckCanAssignInspection() : boolean {
        return this.isTankerInspection ? this.canAssignTankerInspection : false;
    }

    @computedFrom("isRightShipInspection", "canAssignRightShipInspection")
    public get ifRightShipInspectionCheckCanAssignInspection() : boolean {
        return this.isRightShipInspection ? this.canAssignRightShipInspection : false;
    }
    
    @computedFrom("canAssignCargillInspection", "item.inspectionTypeId")
    public get ifCargillAndCheckCanAssignOrAssignedToMe() : boolean {
        return this.ifCargillCheckCanAssignInspection || this.item.assignedUserId == this.userClientService.currentUser.user.id;
    }

    @computedFrom("canChangeCargillInspectionStatus")
    public get ifCargillAndCanChangeStatus(): boolean {
        return this.isCargill ? this.canChangeCargillInspectionStatus : false;
    }

    @computedFrom("canChangeRightShipInspectionStatus")
    public get ifRightShipInspectionAndCanChangeStatus(): boolean {
        return this.isRightShipInspection ? this.canChangeRightShipInspectionStatus : false;
    }

    @computedFrom("canChangeCargillInspectionStatus")
    public get ifTankerAndCanChangeStatus(): boolean {
        return this.isTankerInspection ? this.canChangeCargillInspectionStatus : false;
    }

    @computedFrom("currentStatus.statusId")
    public get isAcceptable(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Acceptable;
    }

    @computedFrom("currentStatus.statusId")
    public get isCanceled(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Cancelled;
    }

    @computedFrom("currentStatus.statusId")
    public get isClosed(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Acceptable || this.currentStatus.statusId == RightShipInspectionStatusEnum.Unacceptable;
    }

    @computedFrom("item.statuses.length")
    public get currentStatus(): RightShipInspectionStatusClientModel {
        if (this.item && this.item.statuses && this.item.statuses.length > 0) {
            return this.item.statuses[0];
        } else return null;
    }

    @computedFrom("currentStatus.statusId")
    public get inProgress(): boolean {
        return this.currentStatus.statusId == RightShipInspectionStatusEnum.InProgress;
    }

    @computedFrom("currentStatus.statusId")
    public get isReviewRequired(): boolean {
        return this.currentStatus.statusId == RightShipInspectionStatusEnum.ReviewRequired;
    }

    @computedFrom("item, item.inspectionTypeId, item.useMobileInspectionWorkflow, isRightshipUser")
    public get shouldShowCustomerFindings(): boolean {
        return !this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection &&  this.item.useMobileInspectionWorkflow;
    }

    @computedFrom('item')
    get isCloseOut() {
        const statuses = [
            RightShipInspectionStatusEnum.Acceptable,
            RightShipInspectionStatusEnum.Unacceptable,
            RightShipInspectionStatusEnum.Cancelled
        ];

        return statuses.indexOf(this.item.statuses[0].statusId) != -1;
    }

    @computedFrom(Reflection.getPropertyPath<RightshipInspection>(x => x.item), Reflection.getPropertyPath<RightshipInspection>(x => x.item.validityExpiryDate),
                Reflection.getPropertyPath<RightshipInspection>(x => x.item.actualDateOfInspection), Reflection.getPropertyPath<RightshipInspection>(x => x.item.requestedDateOfInspection))
    get validUntilText() {
        if (!this.item.validityExpiryDate) return null;
        const dateOfInspection = this.item.actualDateOfInspection ? this.item.actualDateOfInspection : this.item.requestedDateOfInspection;
        return this.inspectionHelper.getValidUntilText(dateOfInspection, this.item.validityExpiryDate);
    }

    @computedFrom(Reflection.getPropertyPath<RightshipInspection>(x => x.item), Reflection.getPropertyPath<RightshipInspection>(x => x.item.vesselDateOfBuild))
    public get age() {
        if (this.item) {
            if (this.item.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.item.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + "y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    @computedFrom(Reflection.getPropertyPath<RightshipInspection>(p => p.item.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.item.vesselId,
        });
    }

    @computedFrom('item', 'item.inspectionTypeId')
    get isCargill() {
        return this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    @computedFrom('item', 'item.inspectionTypeId')
    get isRightShipInspection() {
        return this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection;
    }
    
    @computedFrom('item', 'item.inspectionTypeId')
    get isTankerInspection() {
        return this.item.inspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom('canEditUpdateRightshipInspection','canViewRightShipInspectionCorrespondence')
    get showMenu() {
        switch(this.inspectionType) {
            case "Tanker": if ((this.canEditUpdateTankerInspection && this.canViewTankerInspectionCorrespondence) || (!this.canViewTankerInspectionCorrespondence && this.canEditUpdateTankerInspection)
            || (this.canViewTankerInspectionCorrespondence && !this.canEditUpdateTankerInspection)) {
                return true
        }
        else return false;
            case "RightShip" : if ((this.canEditUpdateRightshipInspection && this.canViewRightShipInspectionCorrespondence) || (!this.canViewRightShipInspectionCorrespondence && this.canEditUpdateRightshipInspection)
            || (this.canViewRightShipInspectionCorrespondence && !this.canEditUpdateRightshipInspection)) {
                return true
        }
        else return false;
            case "Cargill" : return this.canCreateCargillInspection;
        }
    }
    @computedFrom("item", "viewRightShipInspectionCorrespondence", "item.useMobileInspectionWorkflow")
    get menuItems(): MenuItem[] {
        const visible = this.item ? true : false;
        return [
            {
                click: () => this.editRightShipInspection(),
                isVisible: (this.isCargill && this.canEditCargillInspection && this.ifCargillAndCheckCanAssignOrAssignedToMe) || 
                    (this.isRightShipInspection && this.canEditUpdateRightshipInspection) || (this.isTankerInspection && this.canEditUpdateTankerInspection),
                item: {
                    addicon: false,
                    editicon: true,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: false,
                    text: 'Edit'
                },
                classList: '',
            },
            {
                click: () => this.changeRightShipInspectionInspector(),
                isVisible: this.item.inspectingCompanyId > 0 && this.canChangeRSInspectionInspector 
                    && this.item.rightShipInspectionStatusId != RightShipInspectionStatusEnum.InspectionRequired && this.isDry,
                item: {
                    addicon: false,
                    editicon: true,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: false,
                    text: 'Assign / Change Inspector'
                },
                classList: '',
            },
            {
                click: () => this.sendHybridInspectionEmail(),
                isVisible: this.canSendHybridInspectionEmail && this.item.rightShipInspectionStatusId != RightShipInspectionStatusEnum.InspectionRequired 
                    && this.item.useMobileInspectionWorkflow && this.isDry,
                item: {
                    addicon: false,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: true,
                    text: 'Force Hybrid Form'
                },
                classList: '',
            },
            {
                click: () => this.sendCrewMatrixInspectionEmail(),
                isVisible: this.canSendCrewMatrixInspectionEmail && this.item.rightShipInspectionStatusId != RightShipInspectionStatusEnum.InspectionRequired &&
                     this.item.useMobileInspectionWorkflow && this.isDry,
                item: {
                    addicon: false,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: true,
                    text: 'Force Crew Matrix Form'
                },
                classList: '',
            },
            {
                click: () => {

                    if (!this.correspondence.correspondenceEntity) {
                        this.correspondence.activate(this.getCorrespondenceModel());
                     }

                     this.correspondence.addSubtask(false);
                },
                isVisible: 
                    visible && 
                    this.viewRightShipInspectionCorrespondence && 
                    (this.isCargill && this.canCreateCargillInspectionRfi) && !this.isCanceled && !this.isClosed && 
                    this.ifCargillAndCheckCanAssignOrAssignedToMe,
                item: {
                    addicon: true,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: false,
                    text: 'Create RFI'
                },
                classList: '',
            },
            {
                click: () => {

                    if (!this.correspondence.correspondence) {
                        this.correspondence.activate(this.getCorrespondenceModel());
                     }

                     this.correspondence.addSubtask(true);
                },
                isVisible: 
                    visible && 
                    this.viewRightShipInspectionCorrespondence && 
                    (this.isCargill && this.canCreateCargillInspectionRfi) && !this.isCanceled && !this.isClosed && 
                    this.ifCargillAndCheckCanAssignOrAssignedToMe && this.canCreateInternalRfi,
                item: {
                    addicon: true,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: false,
                    text: 'Create Internal RFI'
                },
                classList: '',
            },            
            {
                click: () => { 
                    this.inspectionService.createFormTask(this.item.rightShipInspectionId).then(() => {
                        this.notificationService.show("Form task creation request sent", "", NotificationType.Success, 3000);
                    });
                },
                isVisible: this.canCreateFormTask && this.item.rightShipInspectionStatusId != RightShipInspectionStatusEnum.InspectionRequired && this.item.canForceTabletSync && this.item.useMobileInspectionWorkflow && this.isDry,
                item: {
                    addicon: false,
                    editicon: false,
                    removeicon: false,
                    resendEmailicon: false,
                    emailicon: false,
                    syncicon: true,
                    clipboardicon: false,
                    text: 'Force Tablet Sync'
                },
                classList: '',
            },
            {
                click: () => this.changeInspectionWorkflowUsed(),
                isVisible: this.canChangeInspectionWorkflow && this.item.rightShipInspectionStatusId != RightShipInspectionStatusEnum.InspectionRequired && this.isDry,
                item: {
                    addicon: false,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: false,
                    syncicon: false,
                    clipboardicon: true,
                    text: this.item.useMobileInspectionWorkflow? 'Use Inspection (Paper based) Workflow': 'Use Inspection (Mobile) Workflow'
                },
                classList: '',
            },
            {
                click: () => this.openResendInspectionRequiredEmailDialog(),
                isVisible: this.item.rightShipInspectionStatusId == RightShipInspectionStatusEnum.InspectionRequired,
                item: {
                    addicon: false,
                    editicon: false,
                    removeicon: false,
                    emailicon: false,
                    resendEmailicon: true,
                    syncicon: false,
                    clipboardicon: false,
                    text: 'Resend inspection required email'
                },
                classList: '',
            },
        ].filter(x => x.isVisible);
    }

    @computedFrom('item.inspectionTypeId')
    get isDry() {
        return this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection;
    }
    
    @computedFrom(Reflection.getPropertyPath<RightshipInspection>(z => z._params),
    Reflection.getPropertyPath<RightshipInspection>(z => z._params.length),
    Reflection.getPropertyPath<RightshipInspection>(z => z._params.source))
    public get previousInstructionTitle(): string {
        const source: RightShipInspectionSource = this._params.source;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const title = this.router.history.getState("previousRouteName");

                    if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
                        return title;
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }

    openRightshipInspectionStatusDialog() {
        if (this.isCanceled && !this.canReopenCancelledRSInspection) return;

        if (this.ifRightShipInspectionAndCanChangeStatus || this.ifCargillAndCanChangeStatus || this.ifTankerAndCanChangeStatus) {
            if (this.item.assignedUserId == this.userClientService.currentUser.user.id || this.isClosed || this.isCanceled) {
                const func = () => {
                    const notClosed = this.correspondence.allItems;
                    this.dialogService.hide().then(() => {
                        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipInspectionStatusDialog,
                            {
                                port: this.item.port,
                                date: this.item.actualDateOfInspection || this.item.requestedDateOfInspection ?
                                    new Date(this.item.actualDateOfInspection ? this.item.actualDateOfInspection : this.item.requestedDateOfInspection)
                                    : new Date(),
                                item: this.item,
                                subtaskTypes: Enumerable.from(notClosed).selectMany(x => x.subtaskTypeIds).distinct().toArray(),
                                reload: (() => {
                                    this.getEntity(this.getItemId());
                                }),
                                emails: CloneHelper.clone(this.item.emails),
                                inspectionType: this.inspectionType
                            }, true, false, false, 'right');
                    });
                }
                this.checkIfUnsavedChanges(this.entityItem.currentStep, func);
            } else {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: this.inspectionType + " Inspection",
                    submit: this.assignToMe.bind(this),
                    callback: this.openRightshipInspectionStatusDialog.bind(this),
                    currentAssignment: this.assignment?.assignedEntity.assignedToUserName,
                    canAssign: (this.isRightShipInspection && this.canAssignRightShipInspection) || 
                                (this.isCargill && this.canAssignCargillInspection) || 
                                (this.isTankerInspection && this.canAssignTankerInspection)
                }, true, false, false);
            }
        }
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }

    protected getPropertiesToSkip() {
        return ["statuses"];
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-overview-text"),
                step: 1,
                vm: this.rightshipInspectionOverview,
                getModel: () => { return { item: this.item, assignment: this.assignment } },
                show: true,
                name: "overview"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-inspector-assignment"),
                step: 2,
                vm: this.rightshipInspectionInspectorAssignment,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: this.isRightshipUser && !this.isCargill,
                name: "inspector-assignment"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-crew-matrix-text"),
                step: 3,
                vm: this.rightshipInspectionCrewMatrix,
                getModel: () => { return { item: this.item, assignment: this.assignment } },
                show: () => this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && this.item.useMobileInspectionWorkflow,
                name: "crew-matrix"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-findings-text"),
                step: 4,
                vm: this.rightshipInspectionNonConformity,
                getModel: () => { return { 
                    item: this.item,
                    assignment: this.assignment,
                    assignToMe: this.assignToMe.bind(this),
                    toggleInspectionDetailDrawer: this.toggleInspectionDetailsDrawer.bind(this) 
                } },
                show: () => this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection &&  this.item.useMobileInspectionWorkflow,
                name: "findings"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-inspector-report-text"),
                step: 5,
                vm: this.rightshipInspectionInspectorReport,
                show: () => this.isRightshipUser && this.item && this.item.rightShipInspectionId > 0 && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && this.canAccessRSInspectorReport && this.item.useMobileInspectionWorkflow
            },
            {
                lazyLoad: true,
                title: "Executive Summary",
                step: 6,
                vm: this.rightshipInspectionExecutiveSummary,
                show: () => (this.isRightshipUser || this.item?.customerId == this.userClientService.currentUser?.businessUnit?.customerId) && this.item && this.item.rightShipInspectionId > 0 && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && this.item.useMobileInspectionWorkflow,
                name: "executive-summary"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-deficiencies-text"),
                step: 7,
                vm: this.rightshipInspectionDeficiencies,
                getModel: () => { return { item: this.item, assignment: this.assignment } },
                show: () => this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && !this.item.useMobileInspectionWorkflow,
                name: "deficiencies"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-preliminary-report-text"),
                step: 8,
                vm: this.rightshipInspectionPreliminaryReport,
                show: () => this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && !this.item.useMobileInspectionWorkflow,
                name: "preliminary-report"
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("rightship-inspection-final-report-text"),
                step: 9,
                vm: this.rightshipInspectionFinalReport,
                show: () => this.isRightshipUser && this.item && this.item.inspectionTypeId == RightShipInspectionType.RightShipInspection && !this.item.useMobileInspectionWorkflow,
                name: "final-report"
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.item.correspondenceCount),
                step: 10,
                vm: this._messageCenterOn ? this.rightshipInspectionMessageCenter: this.correspondence,
                getModel: () => {
                    if(this._messageCenterOn) {
                        return {
                            item: this.item,
                            email: this.userClientService.currentUser.user.email
                        };
                    }
                    return this.getCorrespondenceModel();
                },
                show: () => this.isRightshipUser && this.viewRightShipInspectionCorrespondence && this.item ? true : false,
                name: "correspondences"
            },
            {
                lazyLoad: true,
                title: "Email History",
                step: 11,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: () => this.isRightshipUser && this.viewRightShipInspectionEmailHistory && this.item ? true : false
            },
            {
                lazyLoad: false,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("rightship-inspection-documents-text"), this.item.documentsCount),
                step: 12,
                vm: this.rightshipInspectionDocuments,
                show: this.isRightshipUser || this.isCargill,
                getModel: () => {
                    return {
                        item: this.item,
                        assignment: this.assignment,
                        canAddCargillInspectionDocument : this.canAddCargillInspectionDocument,
                        canDeleteCargillInspectionDocument : this.canDeleteCargillInspectionDocument
                    }
                },
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Private Notes", this.item.privateNotesCount),
                show: this.isRightshipUser && this.userClaimsService.hasPermission(Claims.CanViewRightShipInspectionNotes),
                step: 13,
                vm: this.entityComments,
                getModel: () => ({
                    canSubmit: true,
                    entityTypeId: this.item.entityType,
                    entityId: this.item.rightShipInspectionId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("history-trail-text"),
                step: 14,
                vm: this.rightShipInspectionHistoryTrail,
                show: this.isRightshipUser && (this.item.rightShipInspectionId > 0 && this.item.inspectionTypeId == RightShipInspectionType.CargillSurvey ?  
                    this.canViewCargillInspectionHistoryTrail : this.userClaimsService.hasPermission(Claims.CanViewRightShipInspectionHistoryTrail))
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("survey-text"),
                step: 15,
                vm: this.rightshipInspectionSurvey,
                show: this.item.rightShipInspectionId > 0 && this.isRightshipUser && this.item.hasSurveyResponse == true
            },
            {
                lazyLoad: true,
                title: "Outcome",
                step: 16,
                vm: this.rightshipInspectionReport,
                getModel: () => { return { 
                    validUntil: this.item.validityExpiryDate, //valid date
                    validUntilText: this.validUntilText, //valid months
                    statusName: this.item.statuses[0].name,
                    statusId: this.item.statuses[0].statusId,
                    inspectionId: this.item.rightShipInspectionId,
                    summary: this.item.summary,
                    customerId: this.item.customerId,
                    openFindings: this.openFindingsTab.bind(this),
                    closeoutType: this.item.closeoutType,
                }},
                show: !this.isCargill && this.item.useMobileInspectionWorkflow == true && this.item.hasInspectionOutcomeV2,
                name: "outcome"
            },
            {
                lazyLoad: true,
                title: "Findings",
                step: 17,
                vm: this.partialRightshipInspectionCustomerFindings,
                getModel: () => { return { item: this.item, toggleInspectionDetailDrawer: this.toggleInspectionDetailsDrawer.bind(this) } },
                show: () => this.shouldShowCustomerFindings,
                name: "customer-findings"
            }
        ];
    }

    openFindingsTab(questionNumber: string) {
        const tabTitle = this.shouldShowCustomerFindings ? "Findings" : this.i18N.tr("rightship-inspection-findings-text");
        const findingsTab = this.steps.find(x => x.title == tabTitle);
        this.item.questionNumber = questionNumber ? questionNumber: null;
        if(findingsTab) {
            this.entityItem.currentStep = findingsTab;
        }
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            correspondenceEntity: {
                entityId: this.item.rightShipInspectionId,
                entityTypeId: this.item.entityType,
                isInconsistentState: false,
                requestedBy: null,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: this.item.assignedUserId,
                    assignedToUserName: this.item.assignedTo,
                    assignToMe: this.assignToMe.bind(this),
                    isAssignedToMe: () => this.item.assignedUserId == this.userClientService.currentUser.user.id,
                }
            }, 
            name: `${this.inspectionType} Inspection`,
            showAddRFI: this.item && !this.isClosed && !this.isCanceled && (!this.isCargill || (this.canCreateCargillInspectionRfi && this.ifCargillAndCheckCanAssignOrAssignedToMe)),
            hasOutcome: this.isClosed,
            emails: this.item.emails.filter(x => x.isTechnicalManagerEmail == true),
            sendCloseOutEmail: true,
            initialSubtaskId: this.correspondenceSubtaskId,
            newDocumentAdded: () => {
                this.item.documentsCount += 1;
            }
        }
    }

    protected getItem(id: number): Promise<RightShipInspectionClientModel> {
        return this.inspectionService.getRightShipInspectionClientModel(id);
    }

    protected getItemId(): number {
        return this.item ? this.item.rightShipInspectionId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        throw new Error("Method not implemented.");
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.RightShipInspection;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.RightShipInspection;
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));
    }

    protected initializeItemForCreate(): RightShipInspectionClientModel | Promise<RightShipInspectionClientModel> {
        return null;
    }

    sendHybridInspectionEmail(){
        const technicalManagerEmail = this.assignment.item["technicalManagerEmail"];
        const inspectionId = this.assignment.entityId;
        if (technicalManagerEmail != null){
            this.inspectionService.sendInspectionEmail({
                email : technicalManagerEmail,
                inspectionId : inspectionId,
                portName: this.item.port,
                country: this.item.country,
                inspectionDate: this.item.requestedDateOfInspection,
                vesselsName : this.item.vesselName,
                iMO : this.item.vesselImo,
                flag : this.item["vesselFlag"],
                vesselsManager : this.item["technicalManagerName"],
                companyName : this.item.inspectingCompany,
                lastUpdateByVesselsManager : new Date() }).then(() => {
                    this.notificationService.show('Email has been sent', '', NotificationType.Success, 3000);
                });
        }
    }

    sendCrewMatrixInspectionEmail(){
        const technicalManagerEmail = this.assignment.item["technicalManagerEmail"];
        const inspectionId = this.assignment.entityId;
        if (technicalManagerEmail != null){
            this.inspectionService.sendCrewMatrixInspectionEmail({ 
                email : technicalManagerEmail, 
                inspectionId : inspectionId,
                portName: this.item.port,
                country: this.item.country,
                inspectionDate: this.item.requestedDateOfInspection,
                vesselsName : this.item.vesselName,
                iMO : this.item.vesselImo,
                flag : this.item["vesselFlag"],
                vesselsManager : this.item["technicalManagmentCompany"],
                lastUpdateByVesselsManager : new Date() }).then(() => {
                    this.notificationService.show('Email has been sent', '', NotificationType.Success, 3000);
                });
        }
    }

    changeInspectionWorkflowUsed()
    {
        this.isPageLoading = true;
        const request: ChangeInspectionWorkflowRequest = {
            rightShipInspectionId: this.item.rightShipInspectionId,
            useMobileWorkflow: !this.item.useMobileInspectionWorkflow
        }
        this.inspectionService.changeInspectionWorkflow(request).then(() => {
            this.item.useMobileInspectionWorkflow = !this.item.useMobileInspectionWorkflow;
            this.refreshSteps().then(() => {

                if (this.entityItem.currentStep.show instanceof Function && !this.entityItem.currentStep.show() || 
                    !this.entityItem.currentStep.show) {
                    this.entityItem.currentStep = 
                        this.steps.filter(x => {
                            if (this.entityItem.currentStep.show instanceof Function && this.entityItem.currentStep.show ||
                                this.entityItem.currentStep.show)
                            return x;
                        })[0];
                }
            });                
            // this.getEntity(this.getItemId());
            this.isPageLoading = false;
            this.notificationService.show('Used inspection workflow changed Successfully', '', NotificationType.Success, 3000);
        }, e=> { 
            console.error(e);
            this.isPageLoading = false;
            this.notificationService.show('Failed to change inspection workflow used', '', NotificationType.Error, 3000);
        });
    }

    openResendInspectionRequiredEmailDialog() {
        const onSave = (() => {
            this.dialogService.hide();
            this.rightShipInspectionHistoryTrail.refreshData(this.getItemId());
            this.getEntity(this.getItemId());
        }).bind(this);
        this.dialogService.show(RightShipDialogServiceConstants.URLs.ResendInspectionRequiredEmailDialog, {
            item: this.item,
            reload: onSave
        }, true, true, false, "right");
    }

    refreshEmailsInOverview () {}

    editRightShipInspection() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AddRightShipInspection,
        {
            id: this.getItemId(),
            inspectionTypeId: this.item.inspectionTypeId,
            edit: true
        });
    }

    changeRightShipInspectionCompany() {        
        const onSave = (() => {
            this.rightShipInspectionHistoryTrail.refreshData(this.getItemId());
            this.getEntity(this.getItemId());
        }).bind(this);
        const showDialog = () => {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.ChangeRightshipInspectionCompanyDialog, {
                item: this.item,
                onSave: onSave
            }, true, false, false, "right");
        }

        if ( this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.user.id) {
            showDialog();
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                callback: () => showDialog(),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    changeRightShipInspectionInspector()
    {
        const onSave = (() => {
            this.rightShipInspectionHistoryTrail.refreshData(this.getItemId());
            this.getEntity(this.getItemId());
        }).bind(this);
        const showDialog = () => {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.ChangeRightshipInspectionInspectorDialog, {
                item: this.item,
                onSave: onSave
            }, true, false, false, "right");
        }

        if ( this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.user.id) {
            showDialog();
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                callback: () => showDialog(),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    async assignToMe() {
        if (this.assignment) {
            await this.assignment.assignToMe();
            this.item.rightShipInspectionStatusId = RightShipInspectionStatusEnum.InProgress;
            this.item.assignedUserId = this.assignment.assignedEntity.assignedTo;
        }
    }

    private resetTabState() {
        if (window.history && window.history.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
            delete window.history.state["current-tab"];
            window.history.replaceState(window.history.state, "Vessel");
        }
    }

    public onBeforeStepChanged = (currentStep: StepModel) => {
        return new Promise<void>((resolve) => {
            this.checkIfUnsavedChanges(currentStep, () => { this.resetTabState(); resolve(); });
        });
    }

    navigateBack() {
        let goBackCount: number = this.router && this.router.history && this.router.history.getState("skipPreviousRoutes") ?
            this.router.history.getState("skipPreviousRoutes") : 0;
        goBackCount++;
        this.cacheClient.put(TasksConstants.TabsCacheKey, TasksStepsEnum.INSP).then(() => {
            this.router.history.go(-goBackCount);
        })
            .catch(() => {
                this.router.history.go(-goBackCount);
            });
    }

    onAttached() {
        if (this._params && this._params.subtaskId && this._params.subtaskId > 0) {
            this.viewCorrespondenceBySubtaskId(this._params.subtaskId);
            window.history.replaceState(window.history.state, 'Vet Request', window.location.href.replace(`?subtaskId=${this._params.subtaskId}`, ''));
        }

        if (this._params && this._params.step) {
            const step = this.steps.find(x => x.name == this._params.step);
            if (step) this.entityItem.currentStep = step;
        }
        this.signalClientService.subscribe(SignalFunctions.rsDocumentAdded, this.refreshDocumentsCount);
        this.signalClientService.subscribe(SignalFunctions.rightShipInspectionDocumentsParsedChanged, this.rightShipInspectionDocumentsParsedChanged);
        this.signalClientService.subscribe(SignalFunctions.rightShipInspectionCompanyChanged, this.rightShipInspectionCompanyChanged);
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
    }

    onDetached() {
        this.signalClientService.unsubscribe(SignalFunctions.rsDocumentAdded, this.refreshDocumentsCount);
        this.signalClientService.unsubscribe(SignalFunctions.rightShipInspectionDocumentsParsedChanged, this.rightShipInspectionDocumentsParsedChanged);
        this.signalClientService.unsubscribe(SignalFunctions.rightShipInspectionCompanyChanged, this.rightShipInspectionCompanyChanged);
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
    }

    viewCorrespondenceBySubtaskId(subtaskId: number) {
        this.correspondenceSubtaskId = subtaskId;
        const step = this.steps.filter(x => x.vm == this.correspondence)[0];
        this.entityItem.currentStep = step;
    }

    refreshDocumentsCount = (params) => {
        if (this.item.rightShipInspectionId == params.rightShipInspectionId) {
            this.item.documentsCount += params.attachmentsCount;
            if (this.entityItem && this.entityItem.currentStep && this.entityItem.currentStep.step == 6 && this.rightshipInspectionDocuments) {
                this.rightshipInspectionDocuments.loadAttachments();
            }
        }
    }

    rightShipInspectionDocumentsParsedChanged = (params) => {
        if (this.item.rightShipInspectionId == params.id) {
            this.getEntity(this.getItemId());
        }
    }

    rightShipInspectionCompanyChanged = (param) => {
        if (param.rightShipInspectionId == this.item.rightShipInspectionId) {
            this.item.inspectingCompanyId = param.inspectingCompanyId;
        }
    }

    refreshRightShipInspectionStatus = (param: EntityStatusChangeModel) => {
        if (this.item.rightShipInspectionStatusId == RightShipInspectionStatusEnum.Acceptable ||
            this.item.rightShipInspectionStatusId == RightShipInspectionStatusEnum.Unacceptable) return;
        if (this.item.rightShipInspectionId == param.entityId) {

            if (this.isRightshipUser && this.assignment && param.statusId != RightShipInspectionStatusEnum.RightShipInspectionReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            const status = this.rightShipInspectionStatuses.filter(x => x.id == param.statusId)[0];

            if (status) {
                this.item.rightShipInspectionStatus = status.name;
                this.item.rightShipInspectionStatusId = status.id;
                this.item.statuses.splice(0, 0, {
                    previousStatusId: this.currentStatus ? this.currentStatus.statusId : null,
                    date: new Date(),
                    name: status.name,
                    statusId: status.id,
                    user: param.userName ? param.userName : "",
                    subtaskTypes: [],
                    comment: param.comment
                });
            }
        }
    }

    public checkIfUnsavedChanges(currentStep: StepModel, func: () => void) {
        const viewModelsThatCanBeSaved = [this.rightshipInspectionDeficiencies, this.rightshipInspectionNonConformity];
        if (currentStep && currentStep.vm && viewModelsThatCanBeSaved.indexOf(currentStep.vm) > -1) {
            if (currentStep.vm.hasChanges()) {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipInspectionUnsavedChanges, {
                    onSave: () => {
                        return new Promise<void>((resolve) => {
                            currentStep.vm.save().then(() => {
                                func();
                                this.dialogService.hide();
                                resolve();
                            })
                        });
                    },
                    onCancel: () => {
                        currentStep.vm.discardChanges();
                        func();
                        this.dialogService.hide();
                    }
                }, false, false, false);
            } else {
                func();
            }
        } else {
            func();
        }
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    changeValidityDate() {
        const onSave = (() => {
            this.rightShipInspectionHistoryTrail.refreshData(this.getItemId());
            this.getEntity(this.getItemId());
        }).bind(this);
        this.dialogService.show(RightShipDialogServiceConstants.URLs.ChangeRightshipInspectionValidityDate, {
            item: this.item,
            onSave: onSave
        }, true, false, false, 'right');
    }

    onStepClicked = (step: StepModel) => {
        if (step && step.vm == this.rightshipInspectionDocuments) {
            this.rightshipInspectionDocuments.hasInvalidDocs = null;
        }
    }
}
